import { useQueryClient } from "@tanstack/react-query";
import { Spin } from "antd";
import { useGetCommissionGroupsQuery } from "api/TenantSlug/CommissionGroup/hooks.ts";
import { useSetUserCommissionGroupsMutation } from "api/TenantSlug/User/hooks.ts";
import { CommissionGroupSelectSearchable } from "components/UI/Selects/CommissionGroupSelectSearchable/CommissionGroupSelectSearchable.tsx";
import { COMMISSION_GROUP_USER_GROUP } from "localConstants.ts";
import { useMemo, useState } from "react";

interface CommissionGroupCellProps {
    userId: string;
}

export const CommissionGroupCell = ({ userId }: CommissionGroupCellProps) => {
    const queryClient = useQueryClient();
    const [isUpdating, setIsUpdating] = useState(false);

    const { data: commissionGroupsData, isLoading } = useGetCommissionGroupsQuery(
        { filter: { include: "users" } },
        { enabled: !!userId }
    );

    const mutation = useSetUserCommissionGroupsMutation(userId, {
        onMutate: () => setIsUpdating(true),
        onSettled: () => setIsUpdating(false),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getCommissionGroups"] });
            queryClient.invalidateQueries({ queryKey: ["getUsers"] });
        },
    });

    const commissionGroups = commissionGroupsData?.commissionGroups ?? [];

    const currentValue = useMemo(() => {
        const userGroup = commissionGroups.find((group) => group.users?.some((user) => user.id === userId));
        return userGroup?.id ?? COMMISSION_GROUP_USER_GROUP;
    }, [commissionGroups, userId]);

    const handleChange = (value: string) => {
        const commissionGroupIds = value === COMMISSION_GROUP_USER_GROUP ? [] : [value];
        mutation.mutate({ commission_group_ids: commissionGroupIds });
    };

    if (isLoading && !commissionGroups.length) {
        return <Spin size="small" />;
    }

    return (
        <CommissionGroupSelectSearchable
            withDefaultUserGroup
            value={currentValue}
            onChange={handleChange}
            variant="borderless"
            loading={isUpdating}
            disabled={isUpdating}
            className="w-full"
            popupMatchSelectWidth={false}
            onClick={(e) => e.stopPropagation()}
        />
    );
};
